import confetti from "canvas-confetti"

// Owns the progress display and its lifetime; requests and navigation stay with the flow.
export default class FirstPlanAnalysisAnimation {
  // The count does not run at one speed: it surges into each phase of the work and very nearly
  // stalls as that phase lands, which is what real work looks like from outside. One segment per
  // phase (see phases()), plus a short last one that carries the number into the holding band
  // while the assessment request is still out. Fixed durations, so every run is identical.
  // Six seconds including the finish and settled result, when the assessment is ready.
  BEAT_START_MS = 300
  BEAT_SEGMENTS = [
    { to: 26, ms: 1100, phase: 0 },
    { to: 50, ms: 850, phase: 1 },
    { to: 72, ms: 900, phase: 2 },
    { to: 88, ms: 850, phase: 3 },
    { to: 95, ms: 650, phase: 3 }
  ]
  // How much of each segment runs at a flat speed. The rest is eased, so a segment surges and then
  // slows -- but never to a stop, which is what would leave the number sitting still.
  SEGMENT_LINEAR_SHARE = 0.35
  // Once the ramp is done and the plan still has not arrived, the number creeps the last point and
  // waits there rather than freezing mid-count.
  BEAT_HOLD_CEILING = 96.4
  BEAT_HOLD_MS = 3000
  BEAT_FINISH_MS = 350
  // Once the count lands on 100 the screen holds there, ring still turning, so the finished
  // number is read rather than glimpsed on its way off.
  BEAT_SETTLE_MS = 1000
  // How far behind the leading edge of the fill a dot still carries warmth, and how much it
  // swells there. Past that it is plain indigo: read, and done being looked at.
  DOT_GLOW = 0.13
  DOT_SWELL = 0.28
  // indigo-100 while a dot waits its turn, indigo-500 once the count has passed it, fuchsia at
  // the leading edge. An open product sits on indigo-100 as well: packshots are mostly white, and
  // anything lighter leaves them with nothing to sit on.
  WAITING = [224, 231, 255]
  COOL = [99, 102, 241]
  HOT = [217, 122, 250]
  OPEN_BG = [224, 231, 255]
  // The carrier at rest, the carrier fully open, and the ring it sits on. A carrier grows from
  // its resting inner edge outwards, so it swells out of the ring rather than drifting off it.
  CARRIER_REST = 11
  CARRIER_OPEN = 46
  CARRIER_RING = 116
  // The outer-ring dot nearest a carrier steps aside, so each carrier has its own room.
  CARRIER_CLEARANCE = 0.016
  // Where the bubble finishes inflating and starts to collapse, and how far its surface rocks
  // between tall and wide while it settles.
  BLOOM_IN = 0.28
  BLOOM_OUT = 0.72
  BLOOM_WOBBLE = 0.09
  CLOSED = { amount: 0, squash: 0 }
  // How long the status line waits before swapping its text. The fade-out has to have finished by
  // then or the new line is painted over the old one still on its way out, so this is the line's
  // own transition duration with enough slack that a late frame cannot land the swap early.
  PHASE_SWAP_MS = 180

  constructor({ ring, percent, status, carriers, ingredientCount, pairingCount, reduceMotion, onComplete }) {
    this.ring = ring
    this.percent = percent
    this.status = status
    this.carrierElements = carriers
    this.ingredientCount = ingredientCount
    this.pairingCount = pairingCount
    this.reduceMotion = reduceMotion
    this.onComplete = onComplete
    this.dots = []
  }

  // The request has settled; let the animation reach 100 and hold before handing back.
  finish(succeeded) {
    this.finished = true
    this.succeeded = succeeded
  }

  cancel() {
    cancelAnimationFrame(this.beatFrame)
    clearTimeout(this.phaseTimeout)
    this.dots.forEach((dot) => dot.el.remove())
    this.dots = []
  }

  start() {
    this.cancel()
    this.finished = false
    this.succeeded = false
    this.layoutCarriers()
    this.buildRing()

    this.lastPhase = null
    this.shownPercent = 0
    this.setPhase(0)
    if (this.percent) this.percent.textContent = "0"

    let start = null
    let finishFrom = null
    let finishStart = null
    let settledAt = null

    const frame = (now) => {
      // Start from the first painted frame, after the screen has become visible.
      if (start === null) start = now
      const beat = this.beatAt(now - start)
      let percent = beat.percent

      // The plan is in and the ramp has run: take the number the rest of the way. Where the run to
      // 100 starts from is captured once, so it cannot jump backwards on a later frame.
      if (this.finished && beat.rampDone) {
        if (finishStart === null) {
          finishStart = now
          finishFrom = Math.max(percent, this.shownPercent)
        }
        const t = Math.min(1, (now - finishStart) / this.BEAT_FINISH_MS)
        percent = finishFrom + (100 - finishFrom) * t
      }

      this.showPercent(percent)
      // One progress drives everything: the number, how far the ring has filled, and which
      // product is open. They cannot disagree, because there is nothing to disagree with.
      this.paintRing(this.shownPercent / 100)
      this.paintCarriers(this.shownPercent / 100)
      this.setPhase(beat.phase)

      // The count reaching 100 cannot happen before both the ramp has run and the plan has
      // arrived. The beat then holds on a full ring, read rather than glimpsed, and only
      // afterwards hands over to the product guidance.
      if (this.shownPercent >= 100) {
        if (settledAt === null) {
          settledAt = now
          if (this.succeeded) this.celebrateAnalysis()
        }
        if (now - settledAt >= this.BEAT_SETTLE_MS) {
          this.cancel()
          this.onComplete()
          return
        }
      }

      this.beatFrame = requestAnimationFrame(frame)
    }

    this.beatFrame = requestAnimationFrame(frame)
  }

  celebrateAnalysis() {
    if (!this.ring) return

    const rect = this.ring.getBoundingClientRect()
    confetti({
      particleCount: 32,
      spread: 65,
      startVelocity: 18,
      scalar: 0.65,
      ticks: 60,
      colors: ["#6366f1", "#a5b4fc", "#d97afa"],
      origin: {
        x: (rect.left + rect.width / 2) / window.innerWidth,
        y: (rect.top + rect.height / 2) / window.innerHeight
      },
      disableForReducedMotion: true
    })
  }

  // The scheduled number at a given point in the beat, plus the phase that segment belongs to and
  // whether the ramp has finished. Deterministic: the same elapsed time always gives the same
  // number, on every run and every device.
  beatAt(elapsed) {
    // Give the opening 0% time to register before the count begins.
    elapsed = Math.max(0, elapsed - this.BEAT_START_MS)
    let from = 0
    let startedAt = 0

    for (const segment of this.BEAT_SEGMENTS) {
      if (elapsed < startedAt + segment.ms) {
        const t = (elapsed - startedAt) / segment.ms

        return {
          percent: from + (segment.to - from) * (from === 0 ? t : this.segmentEase(t)),
          phase: segment.phase,
          rampDone: false
        }
      }

      from = segment.to
      startedAt += segment.ms
    }

    // Holding: a slow creep of the last point rather than a frozen number.
    const held = Math.min(1, (elapsed - startedAt) / this.BEAT_HOLD_MS)
    const last = this.BEAT_SEGMENTS[this.BEAT_SEGMENTS.length - 1]

    return {
      percent: from + (this.BEAT_HOLD_CEILING - from) * (1 - Math.pow(1 - held, 2)),
      phase: last.phase,
      rampDone: true
    }
  }

  // Fast out of the boundary, slowing towards the next one, with a flat floor under it so the
  // count never comes to a standstill on its way there.
  segmentEase(t) {
    const clamped = Math.max(0, Math.min(1, t))
    const eased = 1 - Math.pow(1 - clamped, 2.4)

    return this.SEGMENT_LINEAR_SHARE * clamped + (1 - this.SEGMENT_LINEAR_SHARE) * eased
  }

  // Monotonic by construction: the number the user has already seen is never undercut.
  showPercent(percent) {
    this.shownPercent = Math.min(100, Math.max(this.shownPercent, percent))
    if (this.percent) this.percent.textContent = String(Math.round(this.shownPercent))
  }

  // Products take evenly spaced slots around the ring, each half a step in from the one before,
  // so the first is reached shortly after the count starts rather than at nothing. The window a
  // carrier is open for narrows as products are added, so two are never open at once.
  layoutCarriers() {
    const count = this.carrierElements.length
    this.bloomHalf = Math.min(0.065, 0.4 / Math.max(1, count))
    this.carriers = this.carrierElements.map((element, index) => {
      const frac = (index + 0.5) / count
      const angle = frac * Math.PI * 2 - Math.PI / 2

      return {
        el: element,
        image: element.querySelector("img"),
        frac: frac,
        cos: Math.cos(angle),
        sin: Math.sin(angle)
      }
    })
  }

  // How far open a carrier is -- 0 while it is a dot, 1 while it is a product -- and how much its
  // surface is still rocking. A bubble inflates past its resting size, rocks between tall and
  // wide while the surface settles, holds, then gives way and collapses. Reduced motion gets the
  // same schedule with the inflation taken out rather than a product appearing from nowhere.
  bloomShape(frac, progress) {
    const u = (progress - (frac - this.bloomHalf)) / (this.bloomHalf * 2)
    if (u <= 0 || u >= 1) return this.CLOSED
    if (this.reduceMotion) return { amount: Math.min(1, Math.min(u, 1 - u) / 0.12), squash: 0 }

    return { amount: this.bloomSize(u), squash: this.bloomSquash(u) }
  }

  bloomSize(u) {
    if (u < this.BLOOM_IN) {
      // Overshoots and comes back to size, the way a surface does when it stops stretching.
      const t = u / this.BLOOM_IN
      const overshoot = 1.7

      return 1 + (overshoot + 1) * Math.pow(t - 1, 3) + overshoot * Math.pow(t - 1, 2)
    }
    if (u < this.BLOOM_OUT) return 1

    // Holds its size, then gives way: slow to leave and quick to finish, not a symmetric fade.
    return 1 - Math.pow((u - this.BLOOM_OUT) / (1 - this.BLOOM_OUT), 2.2)
  }

  // A damped rock that starts the moment inflation ends, so the two never fight, and is spent
  // well before the bubble collapses.
  bloomSquash(u) {
    if (u < this.BLOOM_IN) return 0

    const since = (u - this.BLOOM_IN) / (this.BLOOM_OUT - this.BLOOM_IN)

    return this.BLOOM_WOBBLE * Math.exp(-3.5 * since) * Math.sin(since * Math.PI * 3)
  }

  paintCarriers(progress) {
    this.carriers.forEach((carrier) => {
      const { amount, squash } = this.bloomShape(carrier.frac, progress)
      const size = this.CARRIER_REST + (this.CARRIER_OPEN - this.CARRIER_REST) * amount
      // The inner edge stays where the dot sat, so the product swells out of the ring.
      const radius = this.CARRIER_RING + (size - this.CARRIER_REST) / 2
      const paper = this.ramp(amount, 0.3, 0.5)
      const wide = 1 + squash
      const tall = 1 - squash

      carrier.el.style.width = `${size.toFixed(2)}px`
      carrier.el.style.height = `${size.toFixed(2)}px`
      carrier.el.style.marginLeft = `${(-size / 2).toFixed(2)}px`
      carrier.el.style.marginTop = `${(-size / 2).toFixed(2)}px`
      carrier.el.style.transform = `translate(${(carrier.cos * radius).toFixed(2)}px,${(carrier.sin * radius).toFixed(2)}px) scale(${wide.toFixed(3)},${tall.toFixed(3)})`
      carrier.el.style.backgroundColor = this.rgb(this.blend(this.dotRgb(carrier.frac, progress), this.OPEN_BG, paper))
      if (!carrier.image) return

      // The product fills the bubble as it inflates, but takes the rocking back out of itself: a
      // stretched surface is a bubble, a stretched packshot is a mistake.
      const fill = 0.72 + 0.28 * this.ramp(amount, 0.3, 0.6)

      carrier.image.style.opacity = this.ramp(amount, 0.35, 0.25).toFixed(3)
      carrier.image.style.transform = `scale(${(fill / wide).toFixed(3)},${(fill / tall).toFixed(3)})`
    })
  }

  ramp(value, from, span) {
    return Math.max(0, Math.min(1, (value - from) / span))
  }

  phases() {
    const ingredients = this.ingredientCount
    const pairings = this.pairingCount

    return [
      `Reading ${ingredients} ingredients`,
      pairings > 0 ? `Checking ${pairings} ${pairings === 1 ? "pairing" : "pairings"}` : "Checking how they layer",
      "Matching your skin profile",
      "Calculating your Match Scores"
    ]
  }

  // The phase is chosen by which segment of the beat is running, not by a threshold on the number,
  // so the line and the count can never disagree about which part of the work is under way.
  setPhase(index) {
    if (!this.status) return

    const phases = this.phases()
    const chosen = phases[Math.max(0, Math.min(phases.length - 1, index))]
    if (this.lastPhase === chosen) return

    this.lastPhase = chosen
    clearTimeout(this.phaseTimeout)

    if (this.reduceMotion) {
      this.status.textContent = chosen
      this.status.style.opacity = "1"
      return
    }

    // The first line of the beat has nothing in its way, so it only fades in.
    if (!this.status.textContent.trim()) return this.fadeInPhase(chosen)

    this.status.style.opacity = "0"
    this.phaseTimeout = setTimeout(() => this.fadeInPhase(chosen), this.PHASE_SWAP_MS)
  }

  // Swap the text while the line is invisible, then bring it back up. The text is set in the same
  // frame that pins the opacity at zero, so the new line is never painted over the old one.
  fadeInPhase(text) {
    this.status.style.opacity = "0"
    this.status.textContent = text
    void this.status.offsetHeight
    this.status.style.opacity = "1"
  }

  // Three concentric rings of dots around a clear centre. Sizes, radii and spacing each carry a
  // small fixed jitter, so the field reads as scattered rather than as a diagram, and every ring
  // is rotated half a step from the one inside it so the dots interlock. The layout never moves:
  // only colour and a slight swell travel around it. The innermost radius, less its largest dot,
  // leaves the centre clear for the percentage and the status line.
  buildRing() {
    if (this.dots.length || !this.ring) return

    const rings = [
      { radius: 84, count: 22, size: 6.5 },
      { radius: 100, count: 26, size: 7.5 },
      { radius: this.CARRIER_RING, count: 30, size: 8.5 }
    ]
    let seed = 0

    rings.forEach((ring, ringIndex) => {
      const step = (Math.PI * 2) / ring.count
      const offset = (ringIndex % 2) * (step / 2)
      const outer = ringIndex === rings.length - 1

      for (let i = 0; i < ring.count; i++) {
        const angle = i * step + offset + (this.jitter(seed) - 0.5) * step * 0.22
        const radius = ring.radius + (this.jitter(seed + 977) - 0.5) * 4
        const size = ring.size * (0.78 + this.jitter(seed + 313) * 0.5)
        const frac = (((angle + Math.PI / 2) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) / (Math.PI * 2)

        seed++
        if (outer && this.crowdsCarrier(frac)) continue

        const translate = `translate(${(Math.cos(angle) * radius).toFixed(2)}px,${(Math.sin(angle) * radius).toFixed(2)}px)`
        const dot = document.createElement("span")

        dot.className = "absolute left-1/2 top-1/2 z-10 rounded-full"
        dot.style.width = `${size.toFixed(2)}px`
        dot.style.height = `${size.toFixed(2)}px`
        dot.style.marginLeft = `${(-size / 2).toFixed(2)}px`
        dot.style.marginTop = `${(-size / 2).toFixed(2)}px`
        dot.style.transform = translate
        dot.style.willChange = "background-color, transform"

        this.ring.insertBefore(dot, this.ring.firstChild)
        this.dots.push({ el: dot, frac, translate })
      }
    })
  }

  crowdsCarrier(frac) {
    return this.carriers.some((carrier) => {
      const gap = Math.abs(frac - carrier.frac)

      return Math.min(gap, 1 - gap) < this.CARRIER_CLEARANCE
    })
  }

  // A repeatable value in [0, 1) for a given seed. The field looks scattered but is laid out
  // identically on every run and every device.
  jitter(seed) {
    const value = Math.sin(seed * 12.9898) * 43758.5453

    return value - Math.floor(value)
  }

  // The ring fills clockwise from the top as the count runs, so the field is the progress bar
  // and the number beside it can only be read as progress. Dots ahead of the fill wait in pale
  // indigo; the leading edge runs fuchsia and swells, and settles to plain indigo behind it.
  // Positions never change.
  paintRing(progress) {
    const done = progress >= 0.999

    this.dots.forEach((dot) => {
      const behind = progress - dot.frac
      const t = behind < 0 || done ? 0 : Math.pow(Math.max(0, 1 - behind / this.DOT_GLOW), 2)

      dot.el.style.backgroundColor = this.rgb(this.dotRgb(dot.frac, progress))
      if (!this.reduceMotion) {
        dot.el.style.transform = `${dot.translate} scale(${(1 + this.DOT_SWELL * t).toFixed(3)})`
      }
    })
  }

  // Pale until the count reaches it, warm at the leading edge, plain indigo once the edge has
  // moved on. Once the count lands, the whole ring settles to one colour rather than leaving a
  // warm patch parked at the top for the length of the hold.
  dotRgb(frac, progress) {
    const behind = progress - frac
    if (behind < 0) return this.WAITING

    const near = progress >= 0.999 ? 0 : Math.max(0, 1 - behind / this.DOT_GLOW)

    return this.blend(this.COOL, this.HOT, near * near)
  }

  blend(a, b, t) {
    return a.map((value, index) => Math.round(value + (b[index] - value) * t))
  }

  rgb(values) {
    return `rgb(${values[0]},${values[1]},${values[2]})`
  }

}
